import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SaveIcon from '@mui/icons-material/Save';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Box, Chip, Stack } from '@mui/material';

import { useAuth } from 'contexts/AuthContext';

import CustomButton from 'components/button/CustomButton';
import Circular from 'components/common/CustomCircular';
import DetailPanel from 'components/condition-list/DetailPanel';
import { Condition, Filter } from 'components/condition-list/SearchFilter';
import {
  ConsoleColumn,
  ConsoleDetailDrawer,
  ConsoleHeader,
  ConsoleHeaderAction,
  ConsoleSearchFilter,
  ConsoleTable,
  useQueryInitialFilters,
} from 'components/console';
import {
  CsvBulkUploadDialog,
  CsvTemplateDownloadDialog,
  downloadCsvTemplate,
} from 'components/csv';
import { CsvBulkConfig } from 'components/csv/types';
import {
  AssessmentDetailResult,
  AssessmentListResult,
  CreateAssessmentParams,
  getAssessment,
  listAssessment,
} from 'stores/AssessmentStoreAdapter';
import { MasterTypeResult, getAssessmentType } from 'stores/MasterTypeStoreAdapter';
import { formatDate } from 'utils/DateTimeFormatter';
import { errorDefault } from 'utils/Toastify';

import AssessmentCreateForm, {
  AssessmentCreateFormHandle,
} from './components/AssessmentCreateForm';
import AssessmentDisposalDialog from './components/AssessmentDisposalDialog';
import AssessmentUpdateForm, {
  AssessmentUpdateFormHandle,
} from './components/AssessmentUpdateForm';
import {
  createAssessmentBulkConfig,
  createAssessmentCsvFields,
} from './components/assessmentCsvConfig';

type DrawerMode = 'none' | 'create' | 'detail';

const AssessmentManagementPage: React.FC = () => {
  const { userSession, users } = useAuth();
  const navigate = useNavigate();
  const { assessmentId: routeAssessmentId } = useParams<{ assessmentId?: string }>();
  const [isProgress, setProgress] = useState(true);
  const [assessments, setAssessments] = useState<AssessmentListResult[]>([]);
  const [assessmentTypes, setAssessmentTypes] = useState<MasterTypeResult[]>([]);
  const [selectedAssessment, setSelectedAssessment] = useState<AssessmentDetailResult | null>(null);
  const [drawerMode, setDrawerMode] = useState<DrawerMode>('none');
  const [isEditingAssessment, setIsEditingAssessment] = useState(false);
  const [disposalDialogOpen, setDisposalDialogOpen] = useState(false);
  const [isCsvTemplateDialogOpen, setIsCsvTemplateDialogOpen] = useState(false);
  const [isCsvBulkDialogOpen, setIsCsvBulkDialogOpen] = useState(false);
  // 現在適用中の検索パラメータ（作成/更新/除却/CSV後・再読み込みで同条件を再検索するため保持）
  const [activeParams, setActiveParams] = useState<Record<string, string | boolean>>({});

  const createFormRef = useRef<AssessmentCreateFormHandle>(null);
  const updateFormRef = useRef<AssessmentUpdateFormHandle>(null);

  const canCreate =
    userSession.roles.includes('ADMIN') || userSession.roles.includes('BACK_OFFICE');
  const canDispose =
    userSession.roles.includes('ADMIN') || userSession.roles.includes('BACK_OFFICE');

  // ID指定モード
  const isDirectMode = !!routeAssessmentId;

  useEffect(() => {
    if (isDirectMode) {
      setProgress(true);
      getAssessment(routeAssessmentId)
        .then((detail) => setSelectedAssessment(detail))
        .catch(() => errorDefault('資産詳細の取得に失敗しました'))
        .finally(() => setProgress(false));
    } else {
      getAssessmentType()
        .then(setAssessmentTypes)
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeAssessmentId]);

  const fetchAssessments = (params: Record<string, string | boolean>) => {
    setProgress(true);
    listAssessment(params)
      .then((result) => {
        setAssessments(result);
        setProgress(false);
      })
      .catch(() => {
        setProgress(false);
        errorDefault('資産情報の取得に失敗しました');
      });
  };

  // 現在適用中の検索条件で再検索する
  const refetch = () => fetchAssessments(activeParams);

  const handleSearch = (filters: Filter[]) => {
    closeDrawer();
    const params: Record<string, string | boolean> = {};
    filters.forEach((f) => {
      if (f.key === 'includeDisposed') {
        params[f.key] = true;
      } else {
        params[f.key] = f.value.key;
      }
    });
    setActiveParams(params);
    fetchAssessments(params);
  };

  const handleCreateClick = () => {
    setSelectedAssessment(null);
    setIsEditingAssessment(false);
    setDrawerMode('create');
  };

  const handleRowClick = (row: AssessmentListResult) => {
    setIsEditingAssessment(false);
    setProgress(true);
    getAssessment(row.assessmentId)
      .then((detail) => {
        setSelectedAssessment(detail);
        setDrawerMode('detail');
        setProgress(false);
      })
      .catch(() => {
        setProgress(false);
        errorDefault('資産詳細の取得に失敗しました');
      });
  };

  const closeDrawer = () => {
    setDrawerMode('none');
    setSelectedAssessment(null);
    setIsEditingAssessment(false);
  };

  // 作成・更新後は共通で「フォームを閉じて現在の検索条件で再検索」
  const handleCreated = () => {
    closeDrawer();
    refetch();
  };

  const handleUpdated = () => {
    closeDrawer();
    refetch();
  };

  const handleDisposed = () => {
    setDisposalDialogOpen(false);
    closeDrawer();
    refetch();
  };

  const searchConditions: Condition[] = useMemo(
    () => [
      {
        key: 'assessmentTypeCode',
        name: '資産区分',
        type: 'select' as const,
        color: '#7E57C2',
        values: assessmentTypes.map((t) => ({ key: t.typeCode, name: t.typeLabel })),
      },
      { key: 'assessmentName', name: '資産名', type: 'text' as const, color: '#42A5F5' },
      { key: 'managementNumber1', name: '管理番号1', type: 'text' as const, color: '#26A69A' },
      { key: 'managementNumber2', name: '管理番号2', type: 'text' as const, color: '#66BB6A' },
      { key: 'managementNumber3', name: '管理番号3', type: 'text' as const, color: '#8D6E63' },
      {
        key: 'userId',
        name: '利用者',
        type: 'user-select-single' as const,
        color: '#FF7043',
        users,
      },
      {
        key: 'managerId',
        name: '管理者',
        type: 'user-select-single' as const,
        color: '#AB47BC',
        users,
      },
      { key: 'combinedInformation', name: '情報', type: 'text' as const, color: '#78909C' },
      {
        key: 'includeDisposed',
        name: '除却済みを含む',
        type: 'check' as const,
        color: '#E53935',
        value: { key: 'true', name: '除却済みを含む' },
      },
      {
        key: 'disposedAtFrom',
        name: '除却日From',
        type: 'date-time-picker-start' as const,
        color: '#F44336',
      },
      {
        key: 'disposedAtTo',
        name: '除却日To',
        type: 'date-time-picker-end' as const,
        color: '#D32F2F',
      },
      {
        key: 'createdAtFrom',
        name: '登録日From',
        type: 'date-time-picker-start' as const,
        color: '#1976D2',
      },
      {
        key: 'createdAtTo',
        name: '登録日To',
        type: 'date-time-picker-end' as const,
        color: '#1565C0',
      },
    ],
    [assessmentTypes, users],
  );

  // URL クエリパラメータを初期検索条件として反映（ID指定モードでは無効）
  const { queryParams, initialFilters } = useQueryInitialFilters(searchConditions, {
    selfUserId: userSession.userId,
  });
  useEffect(() => {
    if (isDirectMode) return;
    setActiveParams(queryParams);
    fetchAssessments(queryParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams, isDirectMode]);

  // CSV
  const csvFields = useMemo(
    () => createAssessmentCsvFields(assessmentTypes, users),
    [assessmentTypes, users],
  );
  const csvBulkConfig: CsvBulkConfig<CreateAssessmentParams> | null = useMemo(
    () =>
      assessmentTypes.length > 0 && users.length > 0
        ? createAssessmentBulkConfig(assessmentTypes, users)
        : null,
    [assessmentTypes, users],
  );
  const handleCsvTemplateDownload = () => {
    downloadCsvTemplate(csvFields, '資産一括登録テンプレート.csv');
  };

  // 「アクション」メニュー（一括登録 / テンプレDL）
  const menuActions: ConsoleHeaderAction[] | undefined = canCreate
    ? [
        {
          label: 'CSV一括登録',
          icon: <UploadFileIcon sx={{ fontSize: 18 }} />,
          onClick: () => setIsCsvBulkDialogOpen(true),
        },
        {
          label: 'CSVテンプレートをダウンロード',
          icon: <FileDownloadIcon sx={{ fontSize: 18 }} />,
          onClick: () => setIsCsvTemplateDialogOpen(true),
        },
      ]
    : undefined;

  // テーブル列（多列表示）
  const columns: ConsoleColumn<AssessmentListResult>[] = [
    {
      key: 'assessmentName',
      header: '資産名',
      width: 240,
      render: (a) => (
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
          <Box
            component="span"
            sx={{
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {a.assessmentName}
          </Box>
          {a.isDeleted && (
            <Chip
              label="除却済"
              size="small"
              color="error"
              sx={{ height: 18, fontSize: 10, flexShrink: 0 }}
            />
          )}
        </Stack>
      ),
    },
    {
      key: 'type',
      header: '区分',
      width: 90,
      render: (a) => (
        <Chip
          label={a.assessmentTypeName}
          size="small"
          color="info"
          sx={{ height: 20, fontSize: '0.7rem' }}
        />
      ),
    },
    {
      key: 'management',
      header: '管理番号',
      width: 150,
      ellipsis: (a) =>
        [a.managementNumber1, a.managementNumber2, a.managementNumber3].filter(Boolean).join(' / '),
    },
    {
      key: 'userName',
      header: '利用者',
      width: 140,
      ellipsis: (a) => a.userName || '削除されたユーザー',
    },
    {
      key: 'managerName',
      header: '管理者',
      width: 140,
      ellipsis: (a) => a.managerName || '削除されたユーザー',
    },
    {
      key: 'createdAt',
      header: '登録日',
      width: 110,
      render: (a) => (
        <Box component="span" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {formatDate(a.createdAt)}
        </Box>
      ),
    },
  ];

  // --- ID指定モード（別ウィンドウ等で直接開く）: 従来どおり DetailPanel 表示 ---
  if (isDirectMode) {
    const directFooter =
      selectedAssessment && !selectedAssessment.isDeleted ? (
        isEditingAssessment ? (
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <CustomButton
              label="更新"
              buttonVariant="primary"
              icon={<SaveIcon />}
              onClick={() => updateFormRef.current?.submit()}
            />
          </Stack>
        ) : (
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            {canDispose && (
              <CustomButton
                label="除却"
                buttonVariant="danger"
                icon={<DeleteForeverIcon />}
                onClick={() => setDisposalDialogOpen(true)}
              />
            )}
            <CustomButton
              label="編集"
              buttonVariant="primary"
              icon={<EditIcon />}
              onClick={() => setIsEditingAssessment(true)}
            />
          </Stack>
        )
      ) : undefined;

    return (
      <>
        {selectedAssessment ? (
          <DetailPanel footer={directFooter}>
            <AssessmentUpdateForm
              ref={updateFormRef}
              assessment={selectedAssessment}
              users={users}
              isEditing={isEditingAssessment}
              onUpdated={(updated) => setSelectedAssessment(updated)}
              onClose={() => window.close()}
              onEditingChange={setIsEditingAssessment}
              setProgress={setProgress}
              onDisposalClick={() => setDisposalDialogOpen(true)}
            />
          </DetailPanel>
        ) : (
          <></>
        )}
        {selectedAssessment && (
          <AssessmentDisposalDialog
            open={disposalDialogOpen}
            assessmentId={selectedAssessment.assessmentId}
            onClose={() => setDisposalDialogOpen(false)}
            onDisposed={() => {
              setDisposalDialogOpen(false);
              getAssessment(routeAssessmentId!)
                .then((detail) => setSelectedAssessment(detail))
                .catch(() => errorDefault('資産詳細の取得に失敗しました'));
            }}
            setProgress={setProgress}
          />
        )}
        <Circular open={isProgress} />
      </>
    );
  }

  // ドロワーのフッター（詳細=編集/除却、編集中=更新）
  const drawerFooter =
    drawerMode === 'create' ? (
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <CustomButton label="キャンセル" buttonVariant="secondary" onClick={closeDrawer} />
        <CustomButton
          label="登録"
          buttonVariant="primary"
          icon={<AddIcon />}
          onClick={() => createFormRef.current?.submit()}
        />
      </Stack>
    ) : selectedAssessment && !selectedAssessment.isDeleted ? (
      isEditingAssessment ? (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <CustomButton
            label="キャンセル"
            buttonVariant="secondary"
            onClick={() => setIsEditingAssessment(false)}
          />
          <CustomButton
            label="更新"
            buttonVariant="primary"
            icon={<SaveIcon />}
            onClick={() => updateFormRef.current?.submit()}
          />
        </Stack>
      ) : (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          {canDispose && (
            <CustomButton
              label="除却"
              buttonVariant="danger"
              icon={<DeleteForeverIcon />}
              onClick={() => setDisposalDialogOpen(true)}
            />
          )}
          <CustomButton
            label="編集"
            buttonVariant="primary"
            icon={<EditIcon />}
            onClick={() => setIsEditingAssessment(true)}
          />
        </Stack>
      )
    ) : undefined;

  return (
    <Box sx={{ height: '100%', overflow: 'hidden' }}>
      <Circular open={isProgress} />
      <ConsoleHeader
        title="資産"
        count={assessments.length}
        description="社内資産の登録・更新・除却・検索を行います"
        backLabel="資産管理"
        onBack={() => navigate('/assessment')}
        onRefresh={refetch}
        menuActions={menuActions}
        actions={
          canCreate ? (
            <CustomButton
              label="資産を登録"
              buttonVariant="primary"
              icon={<AddIcon />}
              onClick={handleCreateClick}
            />
          ) : undefined
        }
      />

      <ConsoleTable
        columns={columns}
        rows={assessments}
        rowKey={(a) => a.assessmentId}
        selectedKey={drawerMode === 'detail' ? selectedAssessment?.assessmentId : undefined}
        onRowClick={handleRowClick}
        emptyMessage="資産がありません。「資産を登録」または「CSV一括登録」から登録してください。"
        toolbar={
          <ConsoleSearchFilter
            conditions={searchConditions}
            initialFilters={initialFilters}
            onFiltersChange={handleSearch}
            conditionKey="assessment-list"
            userProfileKey="assessment-list-search"
          />
        }
      />

      {/* 作成 / 詳細・編集 ドロワー */}
      <ConsoleDetailDrawer
        open={drawerMode === 'create'}
        onClose={closeDrawer}
        title="資産の登録"
        width={640}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <AssessmentCreateForm
              ref={createFormRef}
              users={users}
              onCreate={handleCreated}
              onCancel={closeDrawer}
              setProgress={setProgress}
              hideHeader
            />
          </Box>
          <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider', mt: 2 }}>{drawerFooter}</Box>
        </Box>
      </ConsoleDetailDrawer>

      <ConsoleDetailDrawer
        open={drawerMode === 'detail' && selectedAssessment !== null}
        onClose={closeDrawer}
        title={selectedAssessment?.assessmentName ?? ''}
        badges={
          selectedAssessment && (
            <>
              <Chip
                label={selectedAssessment.assessmentTypeName}
                size="small"
                color="info"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
              {selectedAssessment.isDeleted && (
                <Chip
                  label="除却済"
                  size="small"
                  color="error"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
            </>
          )
        }
        width={640}
      >
        {selectedAssessment && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <AssessmentUpdateForm
                ref={updateFormRef}
                assessment={selectedAssessment}
                users={users}
                isEditing={isEditingAssessment}
                onUpdated={handleUpdated}
                onClose={closeDrawer}
                onEditingChange={setIsEditingAssessment}
                setProgress={setProgress}
                onDisposalClick={() => setDisposalDialogOpen(true)}
                hideHeader
              />
            </Box>
            {drawerFooter && (
              <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider', mt: 2 }}>{drawerFooter}</Box>
            )}
          </Box>
        )}
      </ConsoleDetailDrawer>

      {selectedAssessment && (
        <AssessmentDisposalDialog
          open={disposalDialogOpen}
          assessmentId={selectedAssessment.assessmentId}
          onClose={() => setDisposalDialogOpen(false)}
          onDisposed={handleDisposed}
          setProgress={setProgress}
        />
      )}

      <CsvTemplateDownloadDialog
        open={isCsvTemplateDialogOpen}
        onClose={() => setIsCsvTemplateDialogOpen(false)}
        entityName="資産"
        fields={csvFields}
        onDownload={handleCsvTemplateDownload}
      />

      {csvBulkConfig && (
        <CsvBulkUploadDialog
          open={isCsvBulkDialogOpen}
          onClose={() => setIsCsvBulkDialogOpen(false)}
          config={csvBulkConfig}
          onComplete={() => {
            closeDrawer();
            refetch();
          }}
        />
      )}
    </Box>
  );
};

export default AssessmentManagementPage;
